import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, refreshQrsCache } from '../lib/api'
import { clearQrsCache } from '../lib/db'

interface User {
  user_id: number
  rol: string
  sede_id: number
  nombre_completo: string
}

interface AuthStore {
  user: User | null
  token: string | null              // access_token JWT
  refreshToken: string | null       // refresh_token JWT (server-stored, rotation)
  accessExpiresAt: number | null    // epoch ms cuando vence el access (auto-refresh proactivo)
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<string | null> // devuelve nuevo access o null si falla
}

const ROLES_QUE_USAN_QR = new Set(['auxiliar', 'admin'])

export const useAuth = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      accessExpiresAt: null,

      login: async (username, password) => {
        const data = await authApi.login(username, password)
        const expiresAt = Date.now() + data.expires_in * 1000

        // localStorage: api.ts request interceptor lee 'avante_token' para Authorization.
        // refresh queda en zustand persisted (no necesita header) + duplicado en localStorage
        // por si el interceptor lo necesita en el refresh fallback.
        localStorage.setItem('avante_token', data.access_token)
        localStorage.setItem('avante_refresh', data.refresh_token)

        set({
          token: data.access_token,
          refreshToken: data.refresh_token,
          accessExpiresAt: expiresAt,
          user: {
            user_id: data.user_id,
            rol: data.rol,
            sede_id: data.sede_id,
            nombre_completo: data.nombre_completo,
          },
        })

        // Sprint 2 Tarea 2.6.5 Layer 1 — precargar cache de QRs en cada login.
        if (ROLES_QUE_USAN_QR.has(data.rol)) {
          try {
            await refreshQrsCache()
          } catch (err) {
            console.warn('[Auth] no se pudo precargar qrs_cache:', err)
          }
        }
      },

      logout: async () => {
        // 1) Intentar revocar el refresh en servidor (idempotente, ignora errores).
        const refresh = get().refreshToken || localStorage.getItem('avante_refresh')
        if (refresh) {
          try {
            await authApi.logout(refresh)
          } catch {
            // logout server-side falló (offline, refresh inválido). No bloquear logout local.
          }
        }
        // 2) Limpiar estado local siempre.
        localStorage.removeItem('avante_token')
        localStorage.removeItem('avante_refresh')
        await clearQrsCache().catch(() => {})
        set({ user: null, token: null, refreshToken: null, accessExpiresAt: null })
      },

      refresh: async () => {
        // Llamado por el interceptor 401 de api.ts (también puede llamarse proactivo).
        const refresh = get().refreshToken || localStorage.getItem('avante_refresh')
        if (!refresh) return null
        try {
          const data = await authApi.refresh(refresh)
          const expiresAt = Date.now() + data.expires_in * 1000
          localStorage.setItem('avante_token', data.access_token)
          localStorage.setItem('avante_refresh', data.refresh_token)
          set({
            token: data.access_token,
            refreshToken: data.refresh_token,
            accessExpiresAt: expiresAt,
          })
          return data.access_token
        } catch {
          // Refresh inválido/revocado/expirado → forzar re-login.
          localStorage.removeItem('avante_token')
          localStorage.removeItem('avante_refresh')
          set({ user: null, token: null, refreshToken: null, accessExpiresAt: null })
          return null
        }
      },
    }),
    { name: 'avante_auth' },
  ),
)

// ============================================================
// Listener global: cuando api.ts interceptor refresca silenciosamente,
// sincroniza el store zustand con los nuevos tokens.
// ============================================================
if (typeof window !== 'undefined') {
  window.addEventListener('avante:token-refreshed', ((e: CustomEvent) => {
    const { access_token, refresh_token, expires_in } = e.detail
    useAuth.setState({
      token: access_token,
      refreshToken: refresh_token,
      accessExpiresAt: Date.now() + expires_in * 1000,
    })
  }) as EventListener)
}
